import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Select,
  Spinner,
  Switch,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DeleteRegular, SearchRegular } from '@fluentui/react-icons';
import {
  catalogoAPreMarcar,
  type AplicarTiposRequest,
  type AplicarTiposResponse,
  type ClaveDeTipo,
  type ReconocerTiposResponse,
  type Receta,
  type RecetaDesdeLaHoja,
  type TiposDelPlanoResponse,
} from '@supernova/shared';
import { aplicarTiposDelPlano, borrarMemoria, leerTiposDelPlano, reconocerTipos } from '../../api/memoriaTipos.js';
import { listCatalogResources, type CatalogResource } from '../../api/catalogs.js';
import { reconocimientoDe, type ClavesDelElemento } from '../../lib/armadoConTipos.js';
import { ConceptosAsignados } from '../../routes/TiposRecordadosPage.js';
import {
  RECETA_NO_PRESUPUESTAR,
  candidataVigente,
  estadoDelSujeto,
  estadoDeCandidata,
  mismaReceta,
  pasaElFiltro,
  queSeMideDe,
  recetaConComplemento,
  recetaConMagnitud,
  recetaConPrincipal,
  recetaConTextoNuevo,
  recetaSinLinea,
  textoDeEstado,
  type ConceptoElegido,
  type DecisionDelSujeto,
  type EstadoDelSujeto,
  type FiltroDelArbol,
  type PuntoDeEstado,
  type QueSeMide,
} from '../../lib/tiposDelDialogo.js';

/** Un elemento del plano que se puede armar (IFC: un tipo del modelo; DWG: una capa o un bloque). */
export interface ElementoDelDialogo {
  key: string;
  nombre: string;
  medida: string;
  /** IFC: la clase en español («Muros»); DWG: «Capas» o «Bloques». */
  grupo: string;
  /** IFC: la clase IFC (pequeña y gris). */
  claseIfc: string | null;
  claves: ClavesDelElemento;
  /** IFC: el nombre del tipo TAL COMO LO DECLARA EL MODELO (lo que distingue dos tipos que se ven igual: «Muro exterior 15 cm» puede ser varios). null = el modelo no lo nombra. */
  nombreDelTipo?: string | null;
  /** IFC: el nombre que el modelo le dio al tipo, sin la familia («FIRME CONCRETO»): es el título que se ve. null = no lo nombra. */
  nombreVisible?: string | null;
  /** Las piezas del tipo con identificador estable (IFC: GlobalId; DWG: id de la inserción), para darles concepto propio. */
  piezas: { gid: string; nombre: string }[];
  /** Lo que la hoja ya tiene para este elemento (para «Tomar lo que tiene la hoja»). */
  delaHoja: RecetaDesdeLaHoja;
}

export interface CatalogoDelDialogo {
  id: string;
  nombre: string;
}

interface Sujeto {
  id: string;
  nivel: 'clase' | 'tipo' | 'pieza';
  clave: ClaveDeTipo | null;
  /** Solo en 'pieza': la clave de la asignación (`ifc-gid|…` / `dwg-ins|…`). */
  piezaClave?: string;
  /** Solo en 'tipo': sus piezas. */
  piezas?: Sujeto[];
  titulo: string;
  subtitulo: string | null;
  elementos: ElementoDelDialogo[];
}

const useStyles = makeStyles({
  superficie: { maxWidth: '1100px', width: '96vw' },
  cuerpo: { display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '72vh', overflow: 'hidden' },
  catalogos: { display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center' },
  cat: { display: 'flex', alignItems: 'center', gap: '4px' },
  nota: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  dos: { display: 'grid', gridTemplateColumns: 'minmax(260px, 34%) 1fr', gap: '12px', minHeight: 0, flex: 1 },
  arbol: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflowY: 'auto',
    minHeight: '280px',
    maxHeight: '52vh',
    display: 'flex',
    flexDirection: 'column',
  },
  grupo: {
    padding: '6px 10px',
    fontWeight: tokens.fontWeightSemibold,
    background: tokens.colorNeutralBackground3,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  fila: {
    padding: '6px 10px 6px 22px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
  // El elemento seleccionado se nota: barra de color a la izquierda, fondo y letra más fuerte.
  filaSel: {
    background: tokens.colorBrandBackground2,
    boxShadow: `inset 5px 0 0 ${tokens.colorBrandStroke1}`,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': { background: tokens.colorBrandBackground2 },
  },
  filaTexto: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
  punto: { display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  detalle: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px',
    overflowY: 'auto',
    maxHeight: '52vh',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  filtros: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  celda: { padding: '4px 6px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, textAlign: 'left', verticalAlign: 'top' },
  caja: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  aviso: { background: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium, padding: '8px 10px' },
  botones: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  resultado: {
    textAlign: 'left',
    padding: '8px 10px',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
});

const COLOR_DE_PUNTO: Record<PuntoDeEstado, string> = {
  reconocido: tokens.colorPaletteGreenForeground1,
  sugerido: tokens.colorPaletteYellowForeground1,
  conflicto: tokens.colorPaletteYellowForeground1,
  elegido: tokens.colorPaletteGreenForeground1,
  sin: tokens.colorNeutralForeground4,
  roto: tokens.colorPaletteRedForeground1,
};

const TEXTO_DE_PUNTO: Record<PuntoDeEstado, string> = {
  reconocido: 'Con concepto (reconocido de otro proyecto)',
  sugerido: 'Sugerido: confírmalo',
  conflicto: 'Hay dos recordados: elige cuál vale',
  elegido: 'Con concepto asignado',
  sin: 'Sin concepto',
  roto: 'Lo recordado ya no sirve',
};

const QUE_SE_MIDE: { valor: QueSeMide | 'auto'; texto: string }[] = [
  { valor: 'auto', texto: 'Automático' },
  { valor: 'area', texto: 'Área' },
  { valor: 'volumen', texto: 'Volumen' },
  { valor: 'lineal', texto: 'Largo' },
  { valor: 'conteo', texto: 'Piezas' },
  { valor: 'peso', texto: 'Peso' },
];

const fechaCorta = (iso: string) => new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' });

/** Piezas que se listan por tipo (un modelo grande puede tener miles). */
const MAX_PIEZAS_POR_TIPO = 300;

const MOTIVO_DE_SUGERENCIA: Record<string, string> = {
  dwg: 'En un dibujo, lo recordado se sugiere y tú lo confirmas.',
  clase: 'Se recordó para toda esa clase de elementos, no para este tipo.',
  solo_sugerir: 'Pediste que esta se sugiera siempre y nunca se aplique sola.',
  nombre_de_pieza: 'El modelo no nombra el tipo: solo se parece por el nombre de la pieza.',
  uso_no_dicho: 'Uno de los dos modelos no dice para qué se usa el elemento.',
  material_distinto: 'El material que declara el modelo no es el mismo.',
  material_no_dicho: 'Uno de los dos modelos no dice de qué material es.',
};

/** Lo que se va a agregar a la hoja, ya contado (antes de medir: no gasta nada). */
export interface ResumenDeEnvio {
  elementos: number;
  principales: number;
  complementarios: number;
  /** Líneas que se verán en la hoja (ya juntadas por concepto). */
  lineas: number;
  sinConcepto: number;
  yaEnLaHoja: number;
  noPresupuestar: number;
  sugeridos: number;
  conflictos: number;
  noVigentes: number;
}

export interface ResultadoDeEnvio {
  agregados: number;
  sinCantidad: number;
  revisar: number;
  yaEnLaHoja: number;
  fallidos: number;
  /** Suma de lo que devolvió el servidor (sale del precio de cada concepto en el catálogo). */
  importe: number;
}

export interface PonerEnLaHoja {
  /** Cuenta qué entraría, sin medir ni gastar. `{ bloqueo }` = no se puede, y por qué. */
  preparar: (catalogIds: string[]) => Promise<{ resumen: ResumenDeEnvio; datos: unknown } | { bloqueo: string }>;
  /** Mide y agrega a la hoja lo que `preparar` contó. */
  poner: (datos: unknown, avance: (n: number, total: number, etapa: 'midiendo' | 'agregando' | 'actualizando') => void) => Promise<ResultadoDeEnvio>;
}

const unoOVarios = (n: number, uno: string, varios: string) => (n === 1 ? uno : varios.replace('{n}', String(n)));

/** Lo que se va a agregar a la hoja, en palabras. Solo se muestra una línea si su número es mayor que 0. */
function ConfirmacionDeEnvio({ resumen: r, estilo, onPoner, onCancelar }: { resumen: ResumenDeEnvio; estilo: string; onPoner: () => void; onCancelar: () => void }) {
  return (
    <div className={estilo}>
      <b>¿Agregar a la hoja lo asignado?</b>
      {r.lineas > 0 ? (
        <div>
          Se {unoOVarios(r.lineas, 'agrega 1 línea', 'agregan hasta {n} líneas')} para {unoOVarios(r.elementos, '1 elemento', '{n} elementos')}:{' '}
          {unoOVarios(r.principales, '1 concepto principal', '{n} conceptos principales')} y {unoOVarios(r.complementarios, '1 complementario', '{n} complementarios')}.
          {r.lineas > 1 ? ' Las que llevan el mismo concepto se juntan en una sola línea.' : ''}
        </div>
      ) : (
        <div>Ningún elemento tiene un concepto asignado que se pueda usar todavía.</div>
      )}
      {r.sinConcepto > 0 && (
        <div>
          {unoOVarios(r.sinConcepto, '1 elemento no tiene', '{n} elementos no tienen')} concepto asignado: queda fuera. Puedes asignarlo aquí o usar «Presupuestar todo el plano».
        </div>
      )}
      {r.yaEnLaHoja > 0 && <div>{unoOVarios(r.yaEnLaHoja, '1 elemento ya está', '{n} elementos ya están')} en tu hoja: no se tocan.</div>}
      {r.noPresupuestar > 0 && <div>{unoOVarios(r.noPresupuestar, '1 elemento lo marcaste', '{n} elementos los marcaste')} como «No se presupuesta».</div>}
      {r.sugeridos > 0 && <div>{unoOVarios(r.sugeridos, '1 sugerencia sin confirmar no entra', '{n} sugerencias sin confirmar no entran')}: confírmalas con «Usar esto».</div>}
      {r.conflictos > 0 && <div>{unoOVarios(r.conflictos, '1 elemento tiene', '{n} elementos tienen')} dos conceptos posibles: elige uno para que entre.</div>}
      {r.noVigentes > 0 && <div>{unoOVarios(r.noVigentes, '1 elemento tiene', '{n} elementos tienen')} un concepto que ya no sirve (cambió en el catálogo): no entra.</div>}
      {r.lineas > 0 && (
        <>
          <Button size="small" appearance="primary" onClick={onPoner}>
            Agregar a la hoja
          </Button>{' '}
        </>
      )}
      <Button size="small" onClick={onCancelar}>
        {r.lineas > 0 ? 'Cancelar' : 'Cerrar'}
      </Button>
    </div>
  );
}

/** Cómo terminó: lo que entró, lo que quedó sin cantidad o para revisar, y el importe que devolvió el servidor. */
function ResultadoDelEnvio({ r, estilo, formatoPrecio, onVer, onCerrarAviso }: { r: ResultadoDeEnvio; estilo: string; formatoPrecio: (n: number) => string; onVer: () => void; onCerrarAviso: () => void }) {
  const marcadas = Math.max(0, r.revisar - r.sinCantidad);
  const noEntraron = r.fallidos + r.yaEnLaHoja;
  return (
    <div className={estilo}>
      <b>
        {r.agregados === 0 ? 'No entró ninguna línea a tu hoja.' : unoOVarios(r.agregados, 'Listo: entró 1 línea a tu hoja.', 'Listo: entraron {n} líneas a tu hoja.')}
      </b>
      {r.sinCantidad > 0 && (
        <div>
          {unoOVarios(r.sinCantidad, '1 entró', '{n} entraron')} sin cantidad (el plano no da el dato): {r.sinCantidad === 1 ? 'está marcada' : 'están marcadas'} para que la escribas.
        </div>
      )}
      {marcadas > 0 && <div>{unoOVarios(marcadas, '1 entró marcada', '{n} entraron marcadas')} para revisar.</div>}
      {noEntraron > 0 && (
        <div>
          {unoOVarios(noEntraron, '1 no se pudo agregar', '{n} no se pudieron agregar')} (su elemento ya está en la hoja o ya no existe). Vuelve a intentarlo para completar lo que falte.
        </div>
      )}
      {r.agregados > 0 && <div>Importe agregado: {formatoPrecio(r.importe)} (sale del precio de cada concepto en el catálogo).</div>}
      <Button size="small" appearance="primary" onClick={onVer}>
        Ver la hoja
      </Button>{' '}
      <Button size="small" onClick={onCerrarAviso}>
        Cerrar aviso
      </Button>
    </div>
  );
}

export function TiposDelPlano({
  abierto,
  onCerrar,
  projectId,
  fileId,
  esIfc,
  tipoInicial,
  elementos,
  catalogos,
  catalogosMarcados,
  formatoPrecio,
  onAplicado,
  ponerEnLaHoja,
  sugerir,
  onMemoriaCambiada,
}: {
  abierto: boolean;
  onCerrar: () => void;
  projectId: string;
  fileId: string;
  esIfc: boolean;
  /** Clave del elemento (`capa:<nombre>`) con el que se abre: viene del panel «Lo que trae el modelo». */
  tipoInicial?: string | null;
  elementos: ElementoDelDialogo[];
  /** Todos los catálogos accesibles. */
  catalogos: CatalogoDelDialogo[];
  /** Los marcados en el proyecto, por prioridad. */
  catalogosMarcados: string[];
  formatoPrecio: (n: number) => string;
  /** Se aplicó: la pantalla relee lo recordado y, si cambiaron, los catálogos del proyecto. */
  onAplicado: (r: AplicarTiposResponse, catalogIds: string[] | null) => void;
  /** null = no se ofrece «Poner en la hoja lo asignado». */
  ponerEnLaHoja?: PonerEnLaHoja | null;
  /** Recomendados del catálogo para un elemento (como en el resto de la hoja). null = el selector solo busca. */
  sugerir?: SugerirParaElemento | null;
  /** Se quitó una asignación o lo recordado: la pantalla vuelve a contar lo que reconoce. */
  onMemoriaCambiada?: () => void;
}) {
  const s = useStyles();
  // «Poner en la hoja lo asignado»: qué se va a hacer (confirmación), cómo va (progreso) y cómo terminó (resultado).
  const [intencion, setIntencion] = useState<'aplicar' | 'poner'>('aplicar');
  const [preparando, setPreparando] = useState(false);
  const [confirmarEnvio, setConfirmarEnvio] = useState<{ resumen: ResumenDeEnvio; datos: unknown } | null>(null);
  const [enviando, setEnviando] = useState<{ n: number; total: number; etapa: 'midiendo' | 'agregando' | 'actualizando' } | null>(null);
  const [resultadoEnvio, setResultadoEnvio] = useState<ResultadoDeEnvio | null>(null);
  const [cargando, setCargando] = useState(true);
  const [falloCarga, setFalloCarga] = useState(false);
  const [reconocer, setReconocer] = useState<ReconocerTiposResponse | null>(null);
  const [plano, setPlano] = useState<TiposDelPlanoResponse | null>(null);
  const [marcados, setMarcados] = useState<string[]>(catalogosMarcados);
  const [decisiones, setDecisiones] = useState<Map<string, DecisionDelSujeto>>(new Map());
  const [descartadas, setDescartadas] = useState<Set<string>>(new Set());
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<FiltroDelArbol>('todos');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relacionar, setRelacionar] = useState<'principal' | 'complemento' | null>(null);
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  // Lo que ya está asociado a un tipo con OTRO catálogo y se va a reemplazar si la persona lo confirma.
  const [verRecordado, setVerRecordado] = useState(false);
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [quitandoAsignacion, setQuitandoAsignacion] = useState(false);
  const [porReemplazar, setPorReemplazar] = useState<{ titulo: string; concepto: string; catalogo: string; memoriaId: string; version: number }[] | null>(null);

  const marcadosSet = useMemo(() => new Set(marcados), [marcados]);
  const nombreDe = useMemo(() => new Map(catalogos.map((c) => [c.id, c.nombre] as const)), [catalogos]);

  // El árbol: IFC = clase → tipo; DWG = «Capas» / «Bloques» → elemento. La clase IFC también se puede asignar.
  const sujetos = useMemo(() => {
    const porGrupo = new Map<string, ElementoDelDialogo[]>();
    for (const e of elementos) porGrupo.set(e.grupo, [...(porGrupo.get(e.grupo) ?? []), e]);
    const grupos: { grupo: string; clase: Sujeto | null; tipos: Sujeto[] }[] = [];
    // Un grupo del panel puede juntar clases distintas (Puertas y ventanas): cada clase real queda como su propio nodo
    // seleccionable («a toda la clase»), con el mismo nombre de grupo y su clase debajo.
    const porClase: [string, ElementoDelDialogo[]][] = [];
    for (const [grupo, els] of porGrupo) {
      const clases = new Map<string, ElementoDelDialogo[]>();
      for (const e of els) clases.set(e.claseIfc ?? '', [...(clases.get(e.claseIfc ?? '') ?? []), e]);
      for (const g of clases.values()) porClase.push([grupo, g]);
    }
    for (const [grupo, els] of porClase) {
      const claseClave = esIfc ? els[0]?.claves.clase ?? null : null;
      grupos.push({
        grupo,
        clase: claseClave ? { id: `clase|${claseClave.canonica}`, nivel: 'clase', clave: claseClave, titulo: grupo, subtitulo: els[0]?.claseIfc ?? null, elementos: els } : null,
        tipos: els.map((e): Sujeto => ({
          id: `tipo|${e.key}`,
          nivel: 'tipo',
          clave: e.claves.tipo,
          // Igual que el panel «Lo que trae el modelo»: el título es el texto de la pantalla («Muro 24 cm») y debajo, el nombre
          // del tipo TAL COMO LO DECLARA EL MODELO (lo que distingue dos tipos que se ven igual) y la medida.
          titulo: e.nombreVisible ?? e.nombre,
          subtitulo: [e.nombreVisible ? e.nombre : e.nombreDelTipo ?? null, e.medida || null].filter(Boolean).join('\n') || null,
          elementos: [e],
          piezas: e.piezas.slice(0, MAX_PIEZAS_POR_TIPO).map((pz): Sujeto => ({
            id: `pieza|${esIfc ? 'ifc-gid' : 'dwg-ins'}|${pz.gid}`,
            nivel: 'pieza',
            clave: null,
            piezaClave: `${esIfc ? 'ifc-gid' : 'dwg-ins'}|${pz.gid}`,
            titulo: pz.nombre,
            subtitulo: null,
            elementos: [e],
          })),
        })),
      });
    }
    return grupos;
  }, [elementos, esIfc]);
  const todosLosSujetos = useMemo(() => sujetos.flatMap((g) => [...(g.clase ? [g.clase] : []), ...g.tipos, ...g.tipos.flatMap((t) => t.piezas ?? [])]), [sujetos]);
  const porId = useMemo(() => new Map(todosLosSujetos.map((x) => [x.id, x] as const)), [todosLosSujetos]);
  // A quién pertenece cada elemento: un tipo a su clase, una pieza a su tipo.
  const padreDe = useMemo(() => {
    const m = new Map<string, Sujeto>();
    for (const g of sujetos) {
      for (const t of g.tipos) {
        if (g.clase) m.set(t.id, g.clase);
        for (const pz of t.piezas ?? []) m.set(pz.id, t);
      }
    }
    return m;
  }, [sujetos]);

  // Al abrir: lo recordado y lo decidido en este plano (una consulta cada uno).
  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    setCargando(true);
    setFalloCarga(false);
    setError(null);
    setDecisiones(new Map());
    setDescartadas(new Set());
    setMarcados(catalogosMarcados);
    setFiltro('todos');
    setConfirmarCierre(false);
    const vistas = new Map<string, ClaveDeTipo>();
    for (const x of todosLosSujetos) if (x.clave) vistas.set(`${x.clave.canonica}|${x.clave.uso}`, x.clave);
    void Promise.all([vistas.size ? reconocerTipos(projectId, fileId, [...vistas.values()]) : Promise.resolve(null), leerTiposDelPlano(projectId, fileId)])
      .then(([rec, pl]) => {
        if (!vivo) return;
        setReconocer(rec);
        setPlano(pl);
        // Sin selección propia en el proyecto: se propone el catálogo que más reconoce (no se guarda hasta «Aplicar»).
        if (catalogosMarcados.length === 0 && rec) {
          const pre = catalogoAPreMarcar(rec.cobertura, null, catalogos.map((c) => c.id));
          if (pre) setMarcados([pre]);
        }
        setCargando(false);
      })
      .catch(() => {
        if (!vivo) return;
        setFalloCarga(true);
        setCargando(false);
      });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, projectId, fileId]);

  useEffect(() => setConfirmarQuitar(false), [seleccionado]);
  // Al llegar desde una fila del panel «Lo que trae el modelo», el tipo elegido queda a la vista (la lista tiene scroll).
  const yaEnfocado = useRef(false);
  useEffect(() => {
    if (!tipoInicial || yaEnfocado.current || cargando || seleccionado !== `tipo|${tipoInicial}`) return;
    yaEnfocado.current = true;
    requestAnimationFrame(() => document.querySelector('[role="treeitem"][aria-selected="true"]')?.scrollIntoView({ block: 'center' }));
  }, [tipoInicial, cargando, seleccionado]);
  useEffect(() => {
    if (abierto && !seleccionado && todosLosSujetos[0]) {
      const inicial = tipoInicial ? todosLosSujetos.find((x) => x.id === `tipo|${tipoInicial}`) : null;
      setSeleccionado((inicial ?? todosLosSujetos[0]).id);
    }
  }, [abierto, seleccionado, todosLosSujetos]);

  const asignacionDe = (x: Sujeto) =>
    x.nivel === 'pieza'
      ? plano?.asignaciones.find((a) => a.nivel === 'pieza' && a.clave === x.piezaClave)
      : x.clave
        ? plano?.asignaciones.find((a) => a.nivel === x.nivel && a.clave === x.clave!.canonica && a.uso === x.clave!.uso)
        : undefined;
  const estadoDe = (x: Sujeto): EstadoDelSujeto =>
    estadoDelSujeto({
      nivel: x.nivel === 'clase' ? 'clase' : 'tipo',
      decision: decisiones.get(x.id),
      asignacion: asignacionDe(x),
      reconocimiento: x.nivel === 'pieza' ? null : reconocimientoDe(reconocer, x.clave),
      marcados: marcadosSet,
    });

  const resumenDeReceta = (r: Receta, max = 70): string => {
    if (r.noPresupuestar) return 'no se presupuesta';
    const pr = r.lineas.find((l) => l.rol === 'principal');
    return pr ? `${pr.clave} · ${pr.descripcion.length > max ? `${pr.descripcion.slice(0, max - 1)}…` : pr.descripcion}` : '';
  };
  /**
   * El concepto que un elemento USA sin tenerlo propio: un tipo toma el de su clase («Puertas»), una pieza el de su tipo o el de su
   * clase. Lo mismo hace el armado, así que se le dice a la persona en vez de dejar el tipo «sin concepto».
   */
  const heredaDe = (x: Sujeto): { de: 'clase' | 'tipo'; nombre: string; receta: Receta } | null => {
    if (x.nivel === 'clase') return null;
    const propio = estadoDe(x);
    if (propio.punto === 'elegido' || propio.punto === 'reconocido') return null;
    let padre = padreDe.get(x.id);
    while (padre) {
      const e = estadoDe(padre);
      if (e.receta && (e.punto === 'elegido' || e.punto === 'reconocido')) return { de: padre.nivel === 'clase' ? 'clase' : 'tipo', nombre: padre.titulo, receta: e.receta };
      padre = padreDe.get(padre.id);
    }
    return null;
  };

  const hayCambios = decisiones.size > 0 || marcados.join('|') !== catalogosMarcados.join('|');
  const cerrar = () => {
    if (enviando || preparando) return; // no se cierra a medias: se espera a que termine
    if (hayCambios && !confirmarCierre) setConfirmarCierre(true);
    else onCerrar();
  };

  const actual = seleccionado ? porId.get(seleccionado) ?? null : null;
  const est = actual ? estadoDe(actual) : null;
  const decision = actual ? decisiones.get(actual.id) : undefined;

  const decidir = (x: Sujeto, receta: Receta | null, extra?: Partial<DecisionDelSujeto>) => {
    setGuardadoOk(false);
    setDecisiones((prev) => {
      const m = new Map(prev);
      const antes = prev.get(x.id);
      m.set(x.id, { receta, recordar: extra?.recordar ?? antes?.recordar ?? true, soloSugerir: extra?.soloSugerir ?? antes?.soloSugerir ?? false });
      return m;
    });
  };
  const editar = (x: Sujeto, fn: (r: Receta | null) => Receta | null) => decidir(x, fn(estadoDe(x).receta));

  const elegirConcepto = (c: ConceptoElegido) => {
    if (!actual) return;
    const modo = relacionar;
    setRelacionar(null);
    if (modo === 'complemento') editar(actual, (r) => recetaConComplemento(r, c) ?? recetaConPrincipal(null, c));
    else editar(actual, (r) => recetaConPrincipal(r?.noPresupuestar ? null : r, c));
  };

  /** true = se aplicó; false = falló o está esperando que se confirme el reemplazo. */
  const aplicar = async (confirmado = false, cerrarAlTerminar = true): Promise<boolean> => {
    const decs = decisiones;
    // Un tipo tiene UNA sola asociación: recordar otra distinta la reemplaza, y antes se pregunta.
    if (!confirmado) {
      const choques: NonNullable<typeof porReemplazar> = [];
      for (const [id, d] of decs) {
        const x = porId.get(id);
        if (!x?.clave || !d.recordar || !d.receta) continue;
        for (const c of reconocimientoDe(reconocer, x.clave)?.candidatas ?? []) {
          if (c.nivel !== x.nivel || (x.nivel === 'tipo' && c.coincidencia !== 'exacta')) continue;
          if (mismaReceta(c.receta, d.receta) && c.receta.catalogoAncla === d.receta.catalogoAncla) continue;
          const p = c.receta.lineas.find((l) => l.rol === 'principal');
          choques.push({
            titulo: x.titulo,
            concepto: c.receta.noPresupuestar ? 'No se presupuesta' : p ? `${p.clave} · ${p.descripcion}` : '',
            catalogo: (c.receta.catalogoAncla && nombreDe.get(c.receta.catalogoAncla)) || 'otro catálogo',
            memoriaId: c.memoriaId,
            version: c.version,
          });
        }
      }
      if (choques.length > 0) {
        setPorReemplazar(choques);
        return false;
      }
    }
    setPorReemplazar(null);
    setGuardando(true);
    setError(null);
    try {
      const body: AplicarTiposRequest = { asignaciones: [], recordar: [] };
      for (const [id, d] of decs) {
        const x = porId.get(id);
        if (x?.nivel === 'pieza') {
          body.asignaciones.push({ nivel: 'pieza', clave: x.piezaClave!, uso: '', receta: d.receta });
          continue;
        }
        if (!x?.clave) continue;
        const base = reconocimientoDe(reconocer, x.clave)?.candidatas ?? [];
        body.asignaciones.push({ nivel: x.nivel as 'clase' | 'tipo', clave: x.clave.canonica, uso: x.clave.uso, receta: d.receta });
        if (d.recordar && d.receta) {
          const previa = base.find((c) => c.nivel === x.nivel && c.coincidencia === (x.nivel === 'tipo' ? 'exacta' : 'sugerencia'));
          body.recordar.push({ clave: x.clave, receta: d.receta, memoriaId: previa?.memoriaId ?? null, version: previa?.version ?? null, soloSugerir: d.soloSugerir });
        }
      }
      // Siempre se mandan los marcados: la pantalla puede estar usando uno que se propuso pero el proyecto aún no guardó
      // (el servidor valida cada concepto contra los catálogos GUARDADOS del proyecto).
      if (marcados.length > 0) body.catalogIds = marcados;
      const r = await aplicarTiposDelPlano(projectId, fileId, body);
      onAplicado(r, marcados.length > 0 ? marcados : null);
      if (cerrarAlTerminar) {
        onCerrar();
      } else {
        // Sigue abierto: lo que se acaba de guardar ya es «lo asignado», y la persona continúa con el siguiente elemento.
        setDecisiones(new Map());
        await refrescarMemoria();
        setGuardadoOk(true);
      }
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo aplicar. Intenta de nuevo.');
      return false;
    } finally {
      setGuardando(false);
    }
  };

  // «Poner en la hoja lo asignado»: primero se aplica lo que haya cambiado (con su aviso de reemplazo), luego se cuenta qué
  // entraría (sin gastar) y se pide confirmación; solo entonces se mide y se agrega.
  const prepararEnvio = async () => {
    if (!ponerEnLaHoja) return;
    setPreparando(true);
    setError(null);
    try {
      const r = await ponerEnLaHoja.preparar(marcados);
      if ('bloqueo' in r) setError(r.bloqueo);
      else setConfirmarEnvio(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo preparar. Intenta de nuevo.');
    } finally {
      setPreparando(false);
    }
  };
  /** Vuelve a leer lo recordado y lo asignado (sin tocar lo que la persona va decidiendo en el diálogo). */
  const refrescarMemoria = async () => {
    const vistas = new Map<string, ClaveDeTipo>();
    for (const x of todosLosSujetos) if (x.clave) vistas.set(`${x.clave.canonica}|${x.clave.uso}`, x.clave);
    const [rec, pl] = await Promise.all([vistas.size ? reconocerTipos(projectId, fileId, [...vistas.values()]) : Promise.resolve(null), leerTiposDelPlano(projectId, fileId)]);
    setReconocer(rec);
    setPlano(pl);
  };
  /** «Quitar esta asignación»: borra lo recordado de este tipo y lo asignado en este plano, de inmediato. No toca la hoja. */
  const quitarAsignacion = async (x: Sujeto) => {
    setQuitandoAsignacion(true);
    setError(null);
    try {
      if (x.nivel !== 'pieza' && x.clave) {
        for (const c of reconocimientoDe(reconocer, x.clave)?.candidatas ?? []) {
          // Lo recordado de ESTE nivel (en un dibujo también cuenta lo que solo se sugiere); lo de la clase no se toca desde un tipo.
          if (c.nivel !== x.nivel) continue;
          await borrarMemoria(c.memoriaId, c.version);
        }
        await aplicarTiposDelPlano(projectId, fileId, { asignaciones: [{ nivel: x.nivel, clave: x.clave.canonica, uso: x.clave.uso, receta: null }], recordar: [] });
      } else if (x.piezaClave) {
        await aplicarTiposDelPlano(projectId, fileId, { asignaciones: [{ nivel: 'pieza', clave: x.piezaClave, uso: '', receta: null }], recordar: [] });
      }
      setDecisiones((prev) => {
        const m = new Map(prev);
        m.delete(x.id);
        return m;
      });
      await refrescarMemoria();
      onMemoriaCambiada?.();
      setConfirmarQuitar(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo quitar. Intenta de nuevo.');
    } finally {
      setQuitandoAsignacion(false);
    }
  };
  const alPonerEnLaHoja = async () => {
    setIntencion('poner');
    setResultadoEnvio(null);
    if (hayCambios) {
      const ok = await aplicar(false, false);
      if (!ok) return; // falló, o espera la confirmación de reemplazo (su botón sigue el camino)
    }
    await prepararEnvio();
  };
  const confirmarYPoner = async () => {
    if (!ponerEnLaHoja || !confirmarEnvio) return;
    const { datos } = confirmarEnvio;
    setConfirmarEnvio(null);
    setEnviando({ n: 0, total: 0, etapa: 'midiendo' });
    setError(null);
    try {
      setResultadoEnvio(await ponerEnLaHoja.poner(datos, (n, total, etapa) => setEnviando({ n, total, etapa })));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo agregar a la hoja. Lo que ya entró se queda; vuelve a intentarlo para completar lo que falte.');
    } finally {
      setEnviando(null);
    }
  };

  const cobertura = new Map((reconocer?.cobertura ?? []).map((c) => [c.catalogId, c] as const));
  // «N de T»: T son los tipos con clave (el servidor también cuenta las claves de clase).
  const total = todosLosSujetos.filter((x) => x.nivel === 'tipo' && x.clave).length;

  const punto = (p: PuntoDeEstado) => <span className={s.punto} style={{ background: COLOR_DE_PUNTO[p] }} title={TEXTO_DE_PUNTO[p]} aria-label={TEXTO_DE_PUNTO[p]} />;

  const detalle = () => {
    if (!actual || !est) return <span className={s.nota}>Elige un elemento de la izquierda.</span>;
    const candidatas = (reconocimientoDe(reconocer, actual.clave)?.candidatas ?? []).filter((c) => !descartadas.has(`${actual.id}|${c.memoriaId}`));
    const receta = est.receta;
    const unico = actual.elementos[0]!;
    const esPieza = actual.nivel === 'pieza';
    const sinClave = !actual.clave && !esPieza;
    const hoja = actual.nivel === 'tipo' ? unico.delaHoja : null;
    const recordarVisible = !!actual.clave && !!receta;
    const hojaDifiere = !!(hoja?.receta && est.candidata && !mismaReceta(hoja.receta, est.candidata.receta));
    return (
      <>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{actual.titulo}</div>
          <div className={s.nota}>
            {actual.nivel === 'clase'
              ? `Para todos los elementos de esta clase${actual.subtitulo ? ` · ${actual.subtitulo}` : ''}.`
              : esPieza
                ? `Una pieza de «${unico.nombreDelTipo ?? unico.nombre}». ${esIfc ? 'Vale para este modelo y sus revisiones.' : 'Vale solo para este dibujo.'}`
                : [unico.claseIfc, unico.medida].filter(Boolean).join(' · ')}
          </div>
          {esIfc && actual.nivel === 'tipo' && !unico.nombreDelTipo && <div className={s.nota}>El modelo no nombra este tipo.</div>}
        </div>
        {esPieza && (() => {
          const delTipo = estadoDe(porId.get(`tipo|${unico.key}`)!).receta;
          const p = delTipo?.lineas.find((l) => l.rol === 'principal');
          return (
            <div className={s.aviso}>
              {est.punto === 'elegido'
                ? 'Esta pieza lleva su propio concepto en lugar del que tenga su tipo.'
                : `Sin concepto propio: hereda el de su tipo${p ? ` (${p.clave} · ${p.descripcion})` : delTipo?.noPresupuestar ? ' (no se presupuesta)' : ', que todavía no tiene'}.`}
            </div>
          );
        })()}
        {sinClave && (
          <div className={s.aviso}>
            {esIfc ? 'Este modelo no nombra este tipo: solo se puede asignar por clase.' : 'Este elemento no se puede identificar.'}
          </div>
        )}
        {!sinClave && !esPieza && (
          <div className={s.nota}>
            {punto(heredaDe(actual) ? 'elegido' : est.punto)} {heredaDe(actual) ? `Con concepto (el de ${heredaDe(actual)!.de === 'clase' ? 'su clase' : 'su tipo'})` : TEXTO_DE_PUNTO[est.punto]}
            {est.candidata && ` · Lo asignaste${est.candidata.origen.proyecto ? ` en «${est.candidata.origen.proyecto}»` : ''} el ${fechaCorta(est.candidata.origen.fecha)}.`}
            {est.candidata?.origen.porOtraPersona && ` Lo asignó ${est.candidata.origen.porOtraPersona}.`}
          </div>
        )}

        {(() => {
          const h = heredaDe(actual);
          return h ? (
            <div className={s.aviso} style={{ borderColor: tokens.colorPaletteGreenBorder1 }}>
              <b>✓ Este {actual.nivel === 'pieza' ? 'elemento' : 'tipo'} ya usa el concepto que asignaste a {h.de === 'clase' ? `toda la clase «${h.nombre}»` : `su tipo «${h.nombre}»`}.</b>
              <div>{resumenDeReceta(h.receta, Infinity)}</div>
              <div className={s.nota}>Se aplicará solo, sin que hagas nada más. Si quieres un concepto distinto solo para este {actual.nivel === 'pieza' ? 'elemento' : 'tipo'}, elígelo abajo.</div>
            </div>
          ) : null;
        })()}
        {/* Sugerencias, conflicto y lo que ya no sirve */}
        {!sinClave && !esPieza && est.punto !== 'reconocido' && est.punto !== 'elegido' && candidatas.length > 0 && (
          <div className={s.caja}>
            {est.punto === 'conflicto' && <b>«{actual.titulo}» tiene dos conceptos asociados (de catálogos distintos). Elige cuál vale en este plano:</b>}
            {est.punto === 'sugerido' && <b>En otro proyecto este tipo llevó:</b>}
            {est.punto === 'roto' && <b>Lo que recordaste ya no sirve:</b>}
            {candidatas.map((c) => {
              const lineasMalas = c.lineas.map((l, i) => ({ l, i, e: estadoDeCandidata(c, i, marcadosSet) })).filter((x) => x.e !== 'vigente');
              const vigente = candidataVigente(c, marcadosSet);
              const principal = c.receta.lineas.find((l) => l.rol === 'principal');
              return (
                <div key={c.memoriaId} className={s.aviso}>
                  <div>
                    {c.receta.noPresupuestar ? 'No se presupuesta.' : principal ? `${principal.clave} · ${principal.descripcion}` : ''}
                    {c.receta.catalogoAncla && <span className={s.nota}> · del catálogo {nombreDe.get(c.receta.catalogoAncla) ?? 'otro catálogo'}</span>}
                  </div>
                  {c.coincidencia === 'sugerencia' && c.motivo && <div className={s.nota}>{MOTIVO_DE_SUGERENCIA[c.motivo] ?? ''}</div>}
                  {lineasMalas.map(({ l, i, e }) => (
                    <div key={i} className={s.nota} style={{ color: COLOR_DE_PUNTO.roto }}>
                      {c.receta.lineas[i]?.clave}: {textoDeEstado(e)}
                      {e === 'cambio_descripcion' && l.descripcionActual ? ` Ahora dice: «${l.descripcionActual}».` : ''}
                    </div>
                  ))}
                  <div className={s.botones} style={{ marginTop: 6 }}>
                    {vigente && (
                      <Button size="small" appearance="primary" onClick={() => decidir(actual, c.receta)}>
                        Usar esto
                      </Button>
                    )}
                    {!vigente && lineasMalas.every((x) => x.e === 'cambio_descripcion') && (
                      <Button size="small" appearance="primary" onClick={() => decidir(actual, recetaConTextoNuevo(c))}>
                        Usar el texto nuevo
                      </Button>
                    )}
                    {!vigente && (
                      <Button size="small" onClick={() => setRelacionar('principal')}>
                        Elegir otro concepto
                      </Button>
                    )}
                    {est.punto === 'sugerido' && (
                      <Button size="small" appearance="subtle" onClick={() => setDescartadas((p) => new Set(p).add(`${actual.id}|${c.memoriaId}`))}>
                        No, gracias
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* La receta: un principal + sus complementarios */}
        {receta && (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Conceptos de este elemento</div>
            {receta.noPresupuestar ? (
              <div className={s.aviso}>Marcaste que este elemento no se presupuesta.</div>
            ) : (
              <table className={s.tabla}>
                <thead>
                  <tr>
                    {['Clave', 'Concepto', 'Unidad', 'Qué se mide', 'Rol', ''].map((h) => (
                      <th key={h} className={s.celda}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {receta.lineas.map((l) => (
                    <tr key={l.orden}>
                      <td className={s.celda}>{l.clave}</td>
                      <td className={s.celda}>
                        <div>{l.descripcion}</div>
                        <div className={s.nota}>{nombreDe.get(l.catalogId) ?? 'Otro catálogo'}</div>
                      </td>
                      <td className={s.celda}>{l.unidad ?? '—'}</td>
                      <td className={s.celda}>
                        {l.rol === 'principal' && esIfc ? (
                          <Select
                            size="small"
                            value={queSeMideDe(receta) ?? 'auto'}
                            aria-label="Qué se mide"
                            onChange={(_e, d) => editar(actual, (r) => (r ? recetaConMagnitud(r, d.value === 'auto' ? null : (d.value as QueSeMide)) : r))}
                          >
                            {QUE_SE_MIDE.map((o) => (
                              <option key={String(o.valor)} value={String(o.valor)}>
                                {o.texto}
                              </option>
                            ))}
                          </Select>
                        ) : l.rol === 'principal' ? (
                          QUE_SE_MIDE.find((o) => o.valor === (queSeMideDe(receta) ?? 'auto'))?.texto
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={s.celda}>{l.rol === 'principal' ? 'Principal' : 'Complementario'}</td>
                      <td className={s.celda}>
                        <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label={`Quitar ${l.clave}`} onClick={() => editar(actual, (r) => (r ? recetaSinLinea(r, l.orden) : r))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {receta && !receta.noPresupuestar && (
          <div className={s.nota}>
            {receta.lineas.some((l) => l.rol === 'complemento')
              ? 'Quita con el bote los conceptos que no quieras: se guarda y se pone en la hoja solo lo que ves aquí.'
              : 'Los complementarios (aplanado, pintura…) los eliges tú con «+ Complementario…», donde también puedes ver recomendados. Lo que no agregues aquí no entra a la hoja.'}
          </div>
        )}
        {!receta && !sinClave && !esPieza && est.punto === 'sin' && !heredaDe(actual) && <div className={s.nota}>Este elemento todavía no tiene concepto. Al armar se buscará como siempre.</div>}

        <div className={s.botones}>
          <Button size="small" appearance="primary" onClick={() => setRelacionar('principal')}>
            {receta && !receta.noPresupuestar ? 'Cambiar el concepto principal…' : '+ Relacionar concepto…'}
          </Button>
          {receta && !receta.noPresupuestar && (
            <Button size="small" onClick={() => setRelacionar('complemento')}>
              + Complementario…
            </Button>
          )}
          <Button size="small" onClick={() => decidir(actual, RECETA_NO_PRESUPUESTAR)}>
            No presupuestar este elemento
          </Button>
          {hoja?.receta && (
            <Button size="small" title="Toma el concepto y los complementarios que este elemento ya tiene en tu hoja. Después puedes quitar los que no quieras con el bote." onClick={() => decidir(actual, hoja.receta)}>
              Usar lo que ya tengo en la hoja
            </Button>
          )}
          {decision && (
            <Button size="small" appearance="subtle" onClick={() => setDecisiones((p) => { const m = new Map(p); m.delete(actual.id); return m; })}>
              Deshacer mis cambios
            </Button>
          )}
        </div>
        {hoja && hoja.sinCatalogo.length > 0 && (
          <div className={s.nota}>Estos conceptos de la hoja los generó la IA y no se pueden recordar: {hoja.sinCatalogo.join('; ')}.</div>
        )}
        {hoja && hoja.otrosPrincipales.length > 0 && (
          <div className={s.nota}>La hoja tiene otros conceptos principales para este elemento; solo se toma el primero.</div>
        )}
        {hojaDifiere && (
          <div className={s.aviso}>
            En este plano lo cambiaste: ¿actualizar lo recordado?{' '}
            <Button size="small" onClick={() => decidir(actual, hoja!.receta, { recordar: true })}>
              Actualizar lo recordado
            </Button>
          </div>
        )}

        {recordarVisible && (
          <div className={s.nota}>
            Lo que elijas se guarda para este plano y para tus próximos planos con elementos iguales. Los precios no se guardan: salen siempre del catálogo vigente.
          </div>
        )}
        {est.punto !== 'sin' && !sinClave && (
          <div>
            {confirmarQuitar ? (
              <div className={s.aviso}>
                <b>¿Quitar la asignación de «{actual.titulo}»?</b>
                <div>Dejará de aplicarse en este plano y en tus próximos planos. No cambia lo que ya está en tu hoja.</div>
                <Button size="small" appearance="primary" disabled={quitandoAsignacion} onClick={() => void quitarAsignacion(actual)}>
                  {quitandoAsignacion ? 'Quitando…' : 'Sí, quitar'}
                </Button>{' '}
                <Button size="small" disabled={quitandoAsignacion} onClick={() => setConfirmarQuitar(false)}>
                  Conservar
                </Button>
              </div>
            ) : (
              <Button size="small" appearance="subtle" icon={<DeleteRegular />} onClick={() => setConfirmarQuitar(true)}>
                Quitar esta asignación
              </Button>
            )}
          </div>
        )}
      </>
    );
  };

  const cuenta = (p: PuntoDeEstado[]) => todosLosSujetos.filter((x) => x.nivel === 'tipo' && p.includes(estadoDe(x).punto)).length;

  return (
    <>
      <Dialog open={abierto} onOpenChange={(_e, d) => { if (!d.open) cerrar(); }}>
        <DialogSurface className={s.superficie}>
          <DialogBody>
            <DialogTitle>Asignar conceptos a elementos</DialogTitle>
            <DialogContent className={s.cuerpo}>
              {cargando ? (
                <Spinner label="Leyendo lo que recuerdas…" />
              ) : falloCarga ? (
                <div className={s.aviso}>No pudimos leer lo que recuerdas de otros planos. Cierra y vuelve a abrir.</div>
              ) : (
                <>
                  <div className={s.nota} style={{ fontSize: tokens.fontSizeBase300 }}>
                    Elige qué concepto del catálogo lleva cada tipo de elemento (un muro de 24 cm, una ventana…). Lo que elijas se guarda y, cuando abras otro plano con elementos iguales, ya vendrá asignado.
                  </div>
                  <div>
                    {(() => {
                      const fila = (c: CatalogoDelDialogo) => {
                        const cob = cobertura.get(c.id);
                        return (
                          <span key={c.id} className={s.cat}>
                            <Checkbox
                              checked={marcadosSet.has(c.id)}
                              label={c.nombre}
                              onChange={(_e, d) => setMarcados((p) => (d.checked === true ? [...p, c.id] : p.filter((x) => x !== c.id)))}
                            />
                            {cob && total > 0 && (
                              <span className={s.nota}>
                                reconoce {cob.reconoce} de {total}
                                {cob.sugerencias > 0 ? ` y ${cob.sugerencias} sugerencias` : ''}
                              </span>
                            )}
                          </span>
                        );
                      };
                      // Los marcados siempre a la vista; el resto (puede ser una lista larga) se despliega aparte.
                      const puestos = marcados.map((id) => catalogos.find((c) => c.id === id)).filter((c): c is CatalogoDelDialogo => !!c);
                      const otros = catalogos.filter((c) => !marcadosSet.has(c.id));
                      return (
                        <>
                          <div className={s.catalogos}>
                            <b>Catálogos:</b>
                            {puestos.length === 0 && <span className={s.nota}>Ninguno marcado.</span>}
                            {puestos.map(fila)}
                          </div>
                          {otros.length > 0 && (
                            <details>
                              <summary className={s.nota} style={{ cursor: 'pointer' }}>
                                Otros catálogos ({otros.length})
                              </summary>
                              <div className={s.catalogos} style={{ maxHeight: 110, overflowY: 'auto', marginTop: 4 }}>
                                {otros.map(fila)}
                              </div>
                            </details>
                          )}
                        </>
                      );
                    })()}
                    <div className={s.nota}>Solo se usan los marcados, también al buscar un concepto. Desmarcar un catálogo no borra lo que ya recordaste con él.</div>
                    <div className={s.nota}>
                      <b>Guardar</b> deja asignado lo que elegiste (se usará cuando presupuestes este plano y los próximos). <b>Guardar y agregar a la hoja</b> hace lo mismo y además agrega ahora a tu hoja los elementos que ya tienen concepto.
                    </div>
                  </div>

                  <div className={s.filtros}>
                    {(
                      [
                        ['todos', 'Todos'],
                        ['sin', `Sin concepto (${cuenta(['sin'])})`],
                        ['sugeridos', `Sugeridos (${cuenta(['sugerido', 'conflicto'])})`],
                      ] as [FiltroDelArbol, string][]
                    ).map(([f, texto]) => (
                      <Button
                        key={f}
                        size="small"
                        appearance={filtro === f ? 'primary' : 'secondary'}
                        title={f === 'sugeridos' ? 'Elementos que otra vez llevaron un concepto: te lo mostramos y tú confirmas si lo usas.' : f === 'sin' ? 'Elementos que todavía no tienen concepto.' : undefined}
                        onClick={() => setFiltro(f)}
                      >
                        {texto}
                      </Button>
                    ))}
                  </div>

                  <div className={s.dos}>
                    <div className={s.arbol} role="tree" aria-label="Elementos del plano">
                      {sujetos.map((g) => {
                        const visibles = g.tipos.filter((t) => pasaElFiltro(filtro, estadoDe(t).punto));
                        if (visibles.length === 0) return null;
                        return (
                          <div key={`${g.grupo}|${g.clase?.subtitulo ?? ''}`}>
                            <div
                              className={s.grupo}
                              role="treeitem"
                              aria-selected={g.clase ? seleccionado === g.clase.id : false}
                              onClick={() => g.clase && setSeleccionado(g.clase.id)}
                              style={g.clase && seleccionado === g.clase.id ? { background: tokens.colorBrandBackground2, boxShadow: `inset 5px 0 0 ${tokens.colorBrandStroke1}`, fontWeight: tokens.fontWeightSemibold } : undefined}
                            >
                              {g.clase && punto(estadoDe(g.clase).punto)}
                              <span>{g.grupo}</span>
                              {g.clase?.subtitulo && <span className={s.nota}>{g.clase.subtitulo}</span>}
                            </div>
                            {visibles.map((t) => {
                              const piezas = t.piezas ?? [];
                              const abierta = expandidos.has(t.id);
                              const conPropio = piezas.filter((pz) => estadoDe(pz).punto === 'elegido').length;
                              return (
                                <div key={t.id}>
                                  <div
                                    role="treeitem"
                                    aria-selected={seleccionado === t.id}
                                    aria-expanded={piezas.length > 0 ? abierta : undefined}
                                    className={`${s.fila} ${seleccionado === t.id ? s.filaSel : ''}`}
                                    onClick={() => setSeleccionado(t.id)}
                                  >
                                    {punto(heredaDe(t) ? 'elegido' : estadoDe(t).punto)}
                                    <span className={s.filaTexto}>
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.titulo}</span>
                                      {t.subtitulo && <span className={s.nota} style={{ whiteSpace: 'pre-line' }}>{t.subtitulo}</span>}
                                      {heredaDe(t) && (
                                        <span className={s.nota} style={{ color: tokens.colorPaletteGreenForeground1 }} title={resumenDeReceta(heredaDe(t)!.receta, Infinity)}>
                                          ✓ Usa el concepto de {heredaDe(t)!.de === 'clase' ? 'toda la clase' : 'su tipo'}{heredaDe(t)!.receta.lineas.find((l) => l.rol === 'principal') ? ` (${heredaDe(t)!.receta.lineas.find((l) => l.rol === 'principal')!.clave})` : ''}
                                        </span>
                                      )}
                                      {esIfc && !t.elementos[0]!.nombreDelTipo && <span className={s.nota}>El modelo no nombra este tipo</span>}
                                    </span>
                                    {piezas.length > 0 && (
                                      <Button
                                        size="small"
                                        appearance="subtle"
                                        aria-label={`${abierta ? 'Ocultar' : 'Ver'} las piezas de ${t.titulo}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandidos((p) => {
                                            const n = new Set(p);
                                            if (n.has(t.id)) n.delete(t.id);
                                            else n.add(t.id);
                                            return n;
                                          });
                                        }}
                                      >
                                        {abierta ? '▾' : '▸'} {piezas.length} {piezas.length === 1 ? 'pieza' : 'piezas'}
                                        {conPropio > 0 ? ` · ${conPropio} con concepto propio` : ''}
                                      </Button>
                                    )}
                                  </div>
                                  {abierta &&
                                    piezas.map((pz) => (
                                      <div
                                        key={pz.id}
                                        role="treeitem"
                                        aria-selected={seleccionado === pz.id}
                                        className={`${s.fila} ${seleccionado === pz.id ? s.filaSel : ''}`}
                                        style={{ paddingLeft: 44 }}
                                        onClick={() => setSeleccionado(pz.id)}
                                      >
                                        {punto(estadoDe(pz).punto === 'elegido' || heredaDe(pz) ? 'elegido' : 'sin')}
                                        <span className={s.filaTexto}>
                                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pz.titulo}</span>
                                          {estadoDe(pz).punto !== 'elegido' && heredaDe(pz) && (
                                            <span className={s.nota} style={{ color: tokens.colorPaletteGreenForeground1 }}>
                                              ✓ Usa el concepto de {heredaDe(pz)!.de === 'clase' ? 'su clase' : 'su tipo'}
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                    <div className={s.detalle}>{detalle()}</div>
                  </div>

                  <div className={s.nota} style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {(['elegido', 'sugerido', 'sin'] as PuntoDeEstado[]).map((p) => (
                      <span key={p}>
                        {punto(p)} {TEXTO_DE_PUNTO[p]}
                      </span>
                    ))}
                  </div>
                  {error && <div style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</div>}
                  {porReemplazar && (
                    <div className={s.aviso}>
                      {porReemplazar.map((r) => (
                        <div key={r.memoriaId}>
                          «{r.titulo}» ya tiene asociado el concepto {r.concepto} del catálogo {r.catalogo}. ¿Quieres reemplazarlo por el nuevo?
                        </div>
                      ))}
                      <Button
                        size="small"
                        appearance="primary"
                        onClick={() =>
                          void (async () => {
                            const ok = await aplicar(true, false);
                            if (ok && intencion === 'poner') await prepararEnvio();
                          })()
                        }
                      >
                        Reemplazar
                      </Button>{' '}
                      <Button size="small" onClick={() => setPorReemplazar(null)}>
                        No reemplazar
                      </Button>
                    </div>
                  )}
                  {guardadoOk && !hayCambios && <div className={s.aviso}>✓ Guardado. Sigue asignando los demás elementos; cuando termines, cierra.</div>}
                  {preparando && <div className={s.aviso}>Revisando qué entraría a tu hoja…</div>}
                  {confirmarEnvio && <ConfirmacionDeEnvio resumen={confirmarEnvio.resumen} estilo={s.aviso} onPoner={() => void confirmarYPoner()} onCancelar={() => setConfirmarEnvio(null)} />}
                  {enviando && (
                    <div className={s.aviso}>
                      {enviando.etapa === 'midiendo'
                        ? enviando.total > 0
                          ? `Midiendo ${enviando.n} de ${enviando.total}…`
                          : 'Midiendo…'
                        : enviando.etapa === 'agregando'
                          ? `Agregando ${enviando.n} de ${enviando.total} a tu hoja…`
                          : 'Actualizando tu hoja…'}
                    </div>
                  )}
                  {resultadoEnvio && <ResultadoDelEnvio r={resultadoEnvio} estilo={s.aviso} formatoPrecio={formatoPrecio} onVer={onCerrar} onCerrarAviso={() => setResultadoEnvio(null)} />}
                  {confirmarCierre && (
                    <div className={s.aviso}>
                      Tienes cambios sin aplicar. ¿Salir sin guardarlos?{' '}
                      <Button size="small" onClick={onCerrar}>
                        Salir sin guardar
                      </Button>{' '}
                      <Button size="small" appearance="primary" onClick={() => setConfirmarCierre(false)}>
                        Seguir editando
                      </Button>
                    </div>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="transparent" size="small" style={{ marginRight: 'auto' }} onClick={() => setVerRecordado(true)}>
                Ver todo lo que recuerdas
              </Button>
              <Button appearance="secondary" onClick={cerrar} disabled={guardando || preparando || !!enviando}>
                {hayCambios ? 'Cancelar' : 'Cerrar'}
              </Button>
              <Button
                appearance={ponerEnLaHoja ? 'secondary' : 'primary'}
                onClick={() => {
                  setIntencion('aplicar');
                  void aplicar(false, false); // no cierra: sigues asignando
                }}
                disabled={guardando || preparando || !!enviando || cargando || falloCarga || !hayCambios}
              >
                {guardando && intencion === 'aplicar' ? 'Guardando…' : 'Guardar'}
              </Button>
              {ponerEnLaHoja && (
                <Tooltip
                  content={
                    marcados.length === 0
                      ? 'Marca al menos un catálogo.'
                      : 'Guarda lo que elegiste y además agrega ahora a tu hoja los elementos que ya tienen concepto, con la cantidad que mide el plano. Antes te muestro qué se va a agregar. Lo que ya está en tu hoja no se toca.'
                  }
                  relationship="description"
                >
                  <Button
                    appearance="primary"
                    onClick={() => void alPonerEnLaHoja()}
                    disabled={guardando || preparando || !!enviando || cargando || falloCarga || marcados.length === 0}
                  >
                    {guardando && intencion === 'poner' ? 'Guardando…' : preparando ? 'Revisando…' : enviando ? 'Agregando…' : 'Guardar y agregar a la hoja'}
                  </Button>
                </Tooltip>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {verRecordado && (
        <Dialog open onOpenChange={(_e, d) => { if (!d.open) setVerRecordado(false); }}>
          <DialogSurface style={{ maxWidth: '1100px', width: '96vw' }}>
            <DialogBody>
              <DialogTitle>Lo que recuerdas</DialogTitle>
              <DialogContent style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <ConceptosAsignados />
              </DialogContent>
              <DialogActions>
                <Button appearance="primary" onClick={() => setVerRecordado(false)}>
                  Volver
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
      {relacionar && actual && (
        <BuscarConcepto
          catalogos={marcados}
          nombreDe={nombreDe}
          formatoPrecio={formatoPrecio}
          titulo={relacionar === 'principal' ? 'Elegir el concepto principal' : 'Agregar un complementario'}
          onElegir={elegirConcepto}
          onCerrar={() => setRelacionar(null)}
          recomendar={
            sugerir && actual.elementos[0]
              ? (cid) => sugerir(actual.elementos[0]!.key, cid, relacionar === 'complemento' ? receta_principal_descripcion(actual) : undefined)
              : null
          }
        />
      )}
    </>
  );

  /** Complementario: el concepto del elemento al que acompaña (lo que ya tiene elegido), para recomendar lo que le corresponde. */
  function receta_principal_descripcion(x: Sujeto): string | undefined {
    const d = decisiones.get(x.id)?.receta ?? asignacionDe(x)?.receta ?? null;
    return d?.lineas.find((l) => l.rol === 'principal')?.descripcion;
  }
}

/** Los conceptos que el catálogo recomienda para un elemento (cuesta tokens la primera vez: se pide al pulsar). */
export type SugerirParaElemento = (elementoKey: string, catalogId: string, acompanaA?: string) => Promise<{ opciones: (ConceptoElegido & { precio: number | null })[]; nota: string | null }>;

/**
 * «Relacionar concepto»: dos formas de elegir, como en el resto de la hoja. «Buscar» = el buscador de siempre (conceptos, todas las
 * palabras), solo en los catálogos marcados. «Recomendados» = lo que el catálogo recomienda para este elemento, y solo se pide al pulsar
 * el botón. «Generar con IA» no se ofrece aquí: un tipo del modelo no trae el detalle que hace falta; si el concepto no existe, se crea
 * desde la hoja, donde ya tiene su contexto.
 */
function BuscarConcepto({
  catalogos,
  nombreDe,
  formatoPrecio,
  titulo,
  onElegir,
  onCerrar,
  recomendar,
}: {
  catalogos: string[];
  nombreDe: Map<string, string>;
  formatoPrecio: (n: number) => string;
  titulo: string;
  onElegir: (c: ConceptoElegido) => void;
  onCerrar: () => void;
  /** null = este elemento no tiene recomendados (una clase o una pieza): solo se busca. */
  recomendar?: ((catalogId: string) => ReturnType<SugerirParaElemento>) | null;
}) {
  const s = useStyles();
  const [q, setQ] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState<(ConceptoElegido & { precio: number | null })[]>([]);
  const [pestana, setPestana] = useState<'buscar' | 'recomendados'>('buscar');
  const [recomendando, setRecomendando] = useState(false);
  const [recomendados, setRecomendados] = useState<{ opciones: (ConceptoElegido & { precio: number | null })[]; nota: string | null } | null>(null);
  const pedirRecomendados = async () => {
    if (!recomendar || catalogos.length === 0) return;
    setRecomendando(true);
    try {
      const r = await recomendar(catalogos[0]!);
      // Aquí no se genera con IA (un tipo del modelo no trae el detalle): la nota solo manda a «Buscar».
      setRecomendados({ ...r, nota: r.nota ? r.nota.replace(/\s*o genéralo con IA/i, '') : null });
    } catch {
      setRecomendados({ opciones: [], nota: 'No se pudieron obtener recomendados. Intenta de nuevo.' });
    } finally {
      setRecomendando(false);
    }
  };
  const fila = (r: ConceptoElegido & { precio: number | null }) => (
    <button key={`${r.catalogId}:${r.clave}`} type="button" className={s.resultado} onClick={() => onElegir(r)}>
      <span style={{ fontSize: 13 }}>{r.descripcion}</span>
      <span className={s.nota}>
        {r.clave} · {r.unidad ?? 'sin unidad'} · {r.precio != null ? formatoPrecio(r.precio) : 'sin precio'} · {nombreDe.get(r.catalogId) ?? ''}
      </span>
    </button>
  );

  useEffect(() => {
    const texto = q.trim();
    if (texto.length < 2 || catalogos.length === 0) {
      setResultados([]);
      return;
    }
    let vigente = true;
    const t = setTimeout(() => {
      setBuscando(true);
      void Promise.all(
        catalogos.map((cid) =>
          listCatalogResources(cid, { q: texto, tipos: [7], limit: 15, todasLasPalabras: true })
            .then((r) => r.resources.map((x: CatalogResource) => ({ ...x, catalogId: cid })))
            .catch(() => [] as (CatalogResource & { catalogId: string })[]),
        ),
      ).then((listas) => {
        if (!vigente) return;
        const vistos = new Set<string>();
        const out: (ConceptoElegido & { precio: number | null })[] = [];
        for (const r of listas.flat()) {
          if (vistos.has(r.id)) continue;
          vistos.add(r.id);
          out.push({ catalogId: r.catalogId, clave: r.clave, descripcion: r.descripcion, unidad: r.unidad, precio: r.precio });
        }
        setResultados(out.slice(0, 30));
        setBuscando(false);
      });
    }, 300);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [q, catalogos]);

  return (
    <Dialog open onOpenChange={(_e, d) => { if (!d.open) onCerrar(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {catalogos.length === 0 ? (
              <div className={s.aviso}>Marca al menos un catálogo para buscar.</div>
            ) : (
              <>
                {recomendar && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button size="small" appearance={pestana === 'buscar' ? 'primary' : 'secondary'} onClick={() => setPestana('buscar')}>
                      Buscar
                    </Button>
                    <Button size="small" appearance={pestana === 'recomendados' ? 'primary' : 'secondary'} onClick={() => setPestana('recomendados')}>
                      Recomendados
                    </Button>
                  </div>
                )}
                {pestana === 'buscar' || !recomendar ? (
                  <>
                    <Input autoFocus value={q} onChange={(_e, d) => setQ(d.value)} placeholder="Busca un concepto…" contentBefore={<SearchRegular />} aria-label="Buscar concepto" />
                    {buscando && <Spinner size="tiny" label="Buscando…" />}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '46vh', overflowY: 'auto' }}>
                      {resultados.map(fila)}
                      {!buscando && q.trim().length >= 2 && resultados.length === 0 && <span className={s.nota}>No encontramos conceptos con esas palabras.</span>}
                    </div>
                  </>
                ) : (
                  <>
                    {!recomendados && !recomendando && (
                      <>
                        <span className={s.nota}>Te muestro los conceptos de tus catálogos que mejor corresponden a este elemento. Pedirlos usa tokens la primera vez.</span>
                        <span>
                          <Button size="small" appearance="primary" onClick={() => void pedirRecomendados()}>
                            Ver recomendados de mis catálogos
                          </Button>
                        </span>
                      </>
                    )}
                    {recomendando && <Spinner size="tiny" label="Buscando los que mejor corresponden…" />}
                    {recomendados?.nota && <div className={s.aviso}>{recomendados.nota}</div>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '46vh', overflowY: 'auto' }}>
                      {recomendados?.opciones.map(fila)}
                      {recomendados && !recomendando && recomendados.opciones.length === 0 && !recomendados.nota && <span className={s.nota}>No encontramos recomendados para este elemento. Prueba con «Buscar».</span>}
                    </div>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCerrar}>
              Cancelar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
